/**
 * Token storage abstraction so the auth interceptor in createApiClient does
 * not have to know whether it is running in the browser, in Electron's
 * sandboxed renderer, or in a unit test.
 *
 * On web, an instance of WebTokenStore wraps sessionStorage/localStorage. On
 * desktop, the active store will be backed by the OS secure store (Keychain /
 * Credential Vault) — implemented in M3 (Step 6.3 of the developer
 * instructions).
 */

export interface TokenStore {
  /** Read the bearer token. Returns null when the user is not signed in. */
  getToken(): string | Promise<string | null> | null;
  /** Persist a refreshed token, mirroring the storage strategy already in use. */
  setToken(token: string): void | Promise<void>;
  /** Wipe the token (logout / 401 fallthrough). */
  clearToken(): void | Promise<void>;
}

/**
 * Mirrors the existing web behaviour: prefer sessionStorage, fall back to
 * localStorage, and write to whichever bucket already holds the token so
 * "remember me" semantics survive a token refresh.
 */
export class WebTokenStore implements TokenStore {
  getToken(): string | null {
    try {
      return sessionStorage.getItem('token') || localStorage.getItem('token');
    } catch {
      return null;
    }
  }

  setToken(token: string): void {
    try {
      if (sessionStorage.getItem('token')) {
        sessionStorage.setItem('token', token);
      } else {
        localStorage.setItem('token', token);
      }
    } catch {
      /* ignore quota errors */
    }
  }

  clearToken(): void {
    try {
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } catch {
      /* ignore */
    }
  }
}
